import { useState, useRef, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, Bot, User, Sparkles, MessageCircle, ShieldCheck } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { WHATSAPP_URL } from '../config/contact';
import { COMPLIANCE_FULL } from '../config/compliance';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AIMentor() {
  const { token } = useAuth();
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Welcome. I'm the Precision Assistant, your financial and business education companion. I can share general tips and explain how money topics work, from saving and budgeting to tax and retirement. Please note: under FAIS regulations I cannot give personal financial advice. For advice tailored to your situation, chat to a professional on our Financial Advisor WhatsApp Hotline."
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([
    "How do I start investing with a small amount?",
    "What is the 50/30/20 budgeting rule?",
    "How does the tax-free savings account work?"
  ]);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (messageText = input) => {
    if (!messageText.trim() || loading) return;

    const userMessage = { role: 'user', content: messageText };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/mentor/chat`, {
        message: messageText
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const assistantMessage = { role: 'assistant', content: response.data.response };
      setMessages(prev => [...prev, assistantMessage]);
      
      if (response.data.suggestions?.length) {
        setSuggestions(response.data.suggestions);
      }
    } catch (error) {
      toast.error('Failed to get a response. Please try again.');
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: "Apologies, something went wrong on our end. Please try again in a moment."
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <Layout>
      <div className="h-[calc(100vh-64px)] lg:h-screen flex flex-col" data-testid="ai-mentor">
        {/* Header */}
        <div className="p-4 lg:p-6 border-b border-[#C0975C]/20">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center" style={{ backgroundColor: '#0A1F44', border: '1px solid #C0975C' }}>
              <Bot className="w-6 h-6" style={{ color: '#C0975C' }} />
            </div>
            <div>
              <h1 className="font-semibold text-lg" style={{ fontFamily: "'Outfit', Georgia, serif", color: '#F5F1E8' }}>
                Precision Assistant
              </h1>
              <p className="text-[#9fb0cc] text-sm flex items-center gap-1">
                <span className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: '#C0975C' }}></span>
                Financial &amp; business education companion
              </p>
            </div>
          </div>
        </div>

        {/* FAIS compliance banner */}
        <div className="mx-4 lg:mx-6 mt-4 rounded-xl border border-[#C0975C]/30 bg-[#122C5A] p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between" data-testid="fais-banner">
          <p className="text-sm text-[#c6d0e0] flex items-start gap-2">
            <ShieldCheck className="w-5 h-5 text-[#C0975C] flex-shrink-0 mt-0.5" />
            I can share general financial education and tips only. For personal financial advice, please chat to a Precision Wealth adviser on our WhatsApp Hotline.
          </p>
          <a
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="whatsapp-hotline-assistant"
            className="neo-button-primary inline-flex items-center justify-center gap-2 px-5 py-3 whitespace-nowrap text-sm"
          >
            <MessageCircle className="w-4 h-4" /> Advisor WhatsApp Hotline
          </a>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 lg:p-6 space-y-4 hide-scrollbar">
          <AnimatePresence>
            {messages.map((message, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {message.role === 'assistant' && (
                  <div className="w-8 h-8 bg-[#C0975C] flex items-center justify-center flex-shrink-0">
                    <Bot className="w-4 h-4 text-black" />
                  </div>
                )}
                <div 
                  className={`max-w-[80%] lg:max-w-[60%] p-4 ${
                    message.role === 'user' 
                      ? 'bg-[#C0975C] text-black' 
                      : 'neo-card'
                  }`}
                >
                  <p className="text-sm lg:text-base whitespace-pre-wrap">{message.content}</p>
                </div>
                {message.role === 'user' && (
                  <div className="w-8 h-8 bg-[#0A1F44] flex items-center justify-center flex-shrink-0">
                    <User className="w-4 h-4 text-white" />
                  </div>
                )}
              </motion.div>
            ))}
          </AnimatePresence>

          {loading && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex gap-3"
            >
              <div className="w-8 h-8 bg-[#C0975C] flex items-center justify-center">
                <Bot className="w-4 h-4 text-black" />
              </div>
              <div className="neo-card p-4">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-[#C0975C] rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                  <span className="w-2 h-2 bg-[#C0975C] rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                  <span className="w-2 h-2 bg-[#C0975C] rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                </div>
              </div>
            </motion.div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Suggestions */}
        {messages.length < 4 && (
          <div className="px-4 lg:px-6 pb-2">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-[#0A1F44]" />
              <span className="text-[#A3A3A3] text-sm">Quick questions</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {suggestions.map((suggestion, i) => (
                <button
                  key={i}
                  onClick={() => sendMessage(suggestion)}
                  className="text-sm px-3 py-2 border border-white/20 text-[#A3A3A3] hover:border-[#C0975C] hover:text-[#C0975C] transition-colors"
                  data-testid={`suggestion-${i}`}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input */}
        <div className="p-4 lg:p-6 border-t border-white/10">
          <div className="flex gap-3">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask the Precision Assistant about money, investing, tax..."
              className="flex-1 bg-[#0A1F44] border border-[#C0975C]/30 text-[#F5F1E8] px-4 py-3 focus:border-[#C0975C] outline-none transition-colors"
              disabled={loading}
              data-testid="chat-input"
            />
            <button
              onClick={() => sendMessage()}
              disabled={!input.trim() || loading}
              className="neo-button-primary px-6 py-3 disabled:opacity-50"
              data-testid="send-message-btn"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
          <p className="text-[#525252] text-xs mt-2 text-center">
            General education and tips only, not financial advice (FAIS). {COMPLIANCE_FULL} For personal advice, chat to a professional on our Financial Adviser WhatsApp Hotline. Messages are processed per our <a href="/privacy" className="underline" style={{ color: '#C0975C' }}>Privacy Notice</a>.
          </p>
        </div>
      </div>
    </Layout>
  );
}
